import { IconCheck, IconClose, IconGrip, IconPlus, IconShare, IconTrash } from './icons';
import { MUSCLE_KEYS, getMuscleLabel, TIMED_EXERCISES } from './exercises';
import { GuideThumb, GoogleSearchLink } from './ExerciseGuidePreview';
import { findGuideExercise } from './exerciseGuide';
import {
  countLoggedSets,
  formatDuration,
  isSetComplete,
  isSetFilled,
  isTimedExercise,
  workoutHasLoggedSets,
} from './workoutUtils';

function previousLabel(set, weightUnit, timed) {
  if (!set) return '—';
  if (timed) return set.durationMinutes ? `${set.durationMinutes}m` : '—';
  if (!set.reps && !set.weight) return '—';
  return `${set.reps || '—'}×${set.weight || '—'}${weightUnit}`;
}

function recapChangeLabel(recap, t) {
  if (recap?.volumeChangePct == null) return t.recapNoComparison;
  const sign = recap.volumeChangePct >= 0 ? '+' : '';
  const vs = recap.comparedDayName || recap.comparedDate || '';
  return `${sign}${recap.volumeChangePct.toFixed(1)}% ${t.recapVsLast}${vs ? ` · ${vs}` : ''}`;
}

export default function WorkoutSession({
  t,
  language,
  today,
  weightUnit,
  advancedTracking,
  onToggleWeightUnit,
  onToggleAdvanced,
  activeProgram,
  ignoringProgram,
  onPickProgramDay,
  onLogManually,
  onViewProgram,
  todaysExercises,
  workoutFinished,
  onUpdateSet,
  onAddSetRow,
  onDeleteSet,
  onToggleComplete,
  onDeleteExercise,
  onFinish,
  onReopen,
  onDeleteWorkout,
  onBack,
  getLastPerformance,
  exerciseName,
  suggestions,
  onExerciseInput,
  onSelectSuggestion,
  onAddExercise,
  onOpenExercise,
  newExerciseTrackingType,
  onTrackingType,
  customMuscle,
  onCustomMuscle,
  saveState,
  elapsedSeconds,
  defaultRest,
  onChangeDefaultRest,
  selectedProgramDayName,
  showLoadPrevious,
  onToggleLoadPrevious,
  onLoadPrevious,
  previousWorkoutDates,
  workoutLog,
  onShare,
  recap,
  onFillFromPrevious,
  onRequestAiFeedback,
  draggedExerciseIndex,
  dropTargetExerciseIndex,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}) {
  const hasSets = workoutHasLoggedSets({ exercises: todaysExercises });
  const loggedSetCount = countLoggedSets(todaysExercises);

  return (
    <div className="train-tab">
      <div className="train-meta">
        <p className="date-label date-label--inline">{today}{selectedProgramDayName ? ` · ${selectedProgramDayName}` : ''}</p>
        <div className="train-meta-right">
          {elapsedSeconds > 0 && (
            <span className="session-timer" aria-label={t.sessionTime}>{formatDuration(elapsedSeconds)}</span>
          )}
          <span className={`save-indicator save-indicator--${saveState}`}>
            {saveState === 'saving' ? t.saving : t.saved}
          </span>
        </div>
      </div>

      {activeProgram && ignoringProgram && (
        <div className="session-program-bar">
          <div>
            <p className="session-program-label">{t.currentProgram}</p>
            <p className="session-program-day">{selectedProgramDayName || t.logManually}</p>
          </div>
          <div className="session-program-actions">
            {!workoutFinished && todaysExercises.length > 0 && !hasSets && (
              <button type="button" className="btn btn-dark" onClick={onBack}>{t.changeDay}</button>
            )}
            <button type="button" className="btn btn-dark" onClick={onViewProgram}>{t.viewProgram}</button>
          </div>
        </div>
      )}

      {workoutFinished ? (
        <div className="card success">
          <div className="card-header">
            <div>
              <p className="exercise-name" style={{ color: 'var(--success)' }}>{t.workoutComplete}</p>
              <p className="finished-summary">{todaysExercises.length} {t.exercises} · {loggedSetCount} {t.sets}</p>
            </div>
          </div>
          {recap && (
            <div className="session-recap">
              <p className="session-recap-volume">
                {Math.round(recap.volume).toLocaleString()} {weightUnit}
                {recap.durationSeconds > 0 ? ` · ${formatDuration(recap.durationSeconds)}` : ''}
              </p>
              <p className={`session-recap-vs${recap.volumeChangePct != null && recap.volumeChangePct >= 0 ? ' is-up' : ''}`}>
                {recapChangeLabel(recap, t)}
              </p>
              {(recap.muscleUp || recap.muscleDown) && (
                <div className="session-recap-muscles">
                  {recap.muscleUp && (
                    <span>{t.recapMuscleUp} {getMuscleLabel(recap.muscleUp.key, language)} +{recap.muscleUp.delta.toFixed(1)}</span>
                  )}
                  {recap.muscleDown && (
                    <span>{t.recapMuscleDown} {getMuscleLabel(recap.muscleDown.key, language)} {recap.muscleDown.delta.toFixed(1)}</span>
                  )}
                </div>
              )}
              {recap.nextWeight && (
                <p className="session-recap-next">
                  {t.recapNextWeight}: {recap.nextWeight.name} {recap.nextWeight.from} → {recap.nextWeight.to}{recap.nextWeight.unit}
                </p>
              )}
            </div>
          )}
          <div className="finished-detail">
            {todaysExercises.map((ex, i) => (
              <div key={i} className="finished-ex">
                <p className="exercise-name">{ex.name}</p>
                {(ex.sets || []).filter((s) => isSetComplete(ex, s)).map((s, j) => (
                  <div key={j} className="set-row">
                    <span>
                      <span className="set-badge">{t.set} {j + 1}</span>
                      {isTimedExercise(ex) || s.durationMinutes
                        ? `${s.durationMinutes} ${t.min}`
                        : `${s.reps} × ${s.weight}${weightUnit}`}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="expandable-actions">
            <button type="button" className="btn btn-dark" onClick={onShare}>
              <IconShare size={16} /> {t.share}
            </button>
            <button type="button" className="btn btn-dark" onClick={onRequestAiFeedback}>{t.aiFeedback}</button>
            <button type="button" className="btn btn-dark" onClick={onReopen}>{t.reopen}</button>
            <button type="button" className="btn btn-danger" onClick={onDeleteWorkout}>{t.deleteWorkout}</button>
          </div>
        </div>
      ) : (
        <>
          {activeProgram && !ignoringProgram && (
            <div className="active-program-card">
              <p className="active-program-title">{t.activeProgram}</p>
              <div className="active-program-days">
                {activeProgram.days
                  .filter((d) => !d.isRest)
                  .map((day, i) => (
                    <button key={i} type="button" className="btn btn-dark" onClick={() => onPickProgramDay(day)}>
                      {day.dayName}
                    </button>
                  ))}
              </div>
              <div className="active-program-actions">
                <button type="button" className="btn btn-dark" onClick={onLogManually}>{t.logManually}</button>
              </div>
            </div>
          )}

          {todaysExercises.length > 0 && !hasSets && !(activeProgram && ignoringProgram) && (
            <div className="back-row">
              <button type="button" className="btn btn-dark" style={{ width: '100%' }} onClick={onBack}>
                {t.goBack}
              </button>
            </div>
          )}

          {(ignoringProgram || !activeProgram) && (
            <>
              <div className="toolbar">
                <p className="toolbar-title">{t.logExercise}</p>
                <div className="toolbar-controls">
                  <div className="toggle-group">
                    <span className="toggle-label">kg</span>
                    <div className="toggle" role="button" tabIndex={0} onClick={onToggleWeightUnit} onKeyDown={(e) => e.key === 'Enter' && onToggleWeightUnit()} data-active={weightUnit === 'lbs'} aria-label="Weight unit" />
                    <span className="toggle-label">lbs</span>
                  </div>
                  <div className="toggle-group">
                    <span className="toggle-label">{t.advanced}</span>
                    <div className="toggle" role="button" tabIndex={0} onClick={onToggleAdvanced} onKeyDown={(e) => e.key === 'Enter' && onToggleAdvanced()} data-active={advancedTracking} aria-label="Advanced tracking" />
                  </div>
                </div>
              </div>

              <div className="rest-default-row">
                <span className="toggle-label">{t.defaultRest}</span>
                <div className="rest-chips">
                  {[60, 90, 120, 180].map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`rest-chip${Number(defaultRest) === s ? ' is-active' : ''}`}
                      onClick={() => onChangeDefaultRest(s)}
                    >
                      {s}s
                    </button>
                  ))}
                </div>
              </div>

              {todaysExercises.length === 0 && (
                <div className="load-previous-wrap">
                  {!showLoadPrevious ? (
                    <button type="button" className="btn btn-dark" style={{ width: '100%' }} onClick={onToggleLoadPrevious}>
                      {t.loadPreviousWorkout}
                    </button>
                  ) : (
                    <div className="load-previous-card">
                      <p className="load-previous-title">{t.selectWorkout}</p>
                      {previousWorkoutDates.map((date) => (
                        <div
                          key={date}
                          className="load-previous-item"
                          onClick={() => onLoadPrevious(date)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && onLoadPrevious(date)}
                        >
                          <p className="load-previous-date">{date}{workoutLog[date]?.finished ? '' : ` · ${t.incomplete}`}</p>
                          <p className="load-previous-exercises">{(workoutLog[date].exercises || []).map((ex) => ex.name).join(', ')}</p>
                        </div>
                      ))}
                      <button type="button" className="btn btn-dark" style={{ width: '100%', marginTop: 'var(--space-2)' }} onClick={onToggleLoadPrevious}>
                        {t.cancel}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="input-group add-exercise-bar">
                <div className="suggestion-wrap">
                  <input
                    className="input-field wide"
                    placeholder={t.exercisePlaceholder}
                    value={exerciseName}
                    onChange={(e) => onExerciseInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onAddExercise();
                    }}
                  />
                  {suggestions.length > 0 && (
                    <div className="suggestion-list">
                      {suggestions.map((s) => (
                        <div
                          key={s}
                          className="suggestion-item"
                          onClick={() => onSelectSuggestion(s)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && onSelectSuggestion(s)}
                        >
                          <GuideThumb name={s} onOpen={onOpenExercise} size={40} />
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="tracking-toggle-row">
                  <button
                    type="button"
                    className={`btn btn-dark tracking-btn${newExerciseTrackingType === 'weighted' ? ' is-active' : ''}`}
                    onClick={() => onTrackingType('weighted')}
                  >
                    {t.trackingWeighted}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-dark tracking-btn${newExerciseTrackingType === 'timed' ? ' is-active' : ''}`}
                    onClick={() => onTrackingType('timed')}
                  >
                    {t.trackingTimed}
                  </button>
                </div>
                {exerciseName && !TIMED_EXERCISES.has(exerciseName) && newExerciseTrackingType !== 'timed' && (
                  <select
                    className="input-field wide muscle-select"
                    value={customMuscle}
                    onChange={(e) => onCustomMuscle(e.target.value)}
                    aria-label={t.primaryMuscle}
                  >
                    <option value="">{t.primaryMuscleOptional}</option>
                    {MUSCLE_KEYS.map((key) => (
                      <option key={key} value={key}>{getMuscleLabel(key, language)}</option>
                    ))}
                  </select>
                )}
                <div className="add-exercise-actions">
                  {exerciseName.trim() && !findGuideExercise(exerciseName) && (
                    <GoogleSearchLink name={exerciseName.trim()} language={language}>
                      {t.searchGoogle}
                    </GoogleSearchLink>
                  )}
                  <button type="button" className="btn btn-primary" onClick={onAddExercise} disabled={!exerciseName.trim()}>
                    <IconPlus size={16} /> {t.addExercise}
                  </button>
                </div>
              </div>

              {todaysExercises.length > 0 && (
                <>
                  <p className="section-title">{t.todaysWorkout}</p>
                  {todaysExercises.map((ex, i) => {
                    const last = getLastPerformance(ex.name) || [];
                    const timed = isTimedExercise(ex);
                    return (
                      <div
                        key={`${ex.name}-${i}`}
                        data-index={i}
                        onDragOver={(e) => onDragOver(e, i)}
                        onDrop={(e) => onDrop(e, i)}
                        className={`card exercise-log-card${draggedExerciseIndex === i ? ' is-dragging' : ''}${dropTargetExerciseIndex === i && draggedExerciseIndex !== null ? ' is-drop-target' : ''}`}
                      >
                        <div className="exercise-log-head">
                          <span
                            className="drag-handle"
                            draggable
                            onDragStart={(e) => onDragStart(e, i)}
                            onDragEnd={onDragEnd}
                            aria-label={t.dragExercise}
                          >
                            <IconGrip size={16} />
                          </span>
                          <GuideThumb name={ex.name} onOpen={onOpenExercise} />
                          <div className="exercise-log-titles">
                            <p className="exercise-name">{ex.name}</p>
                            {ex.setsReps && <p className="exercise-prescription">{t.repsLabel} {ex.setsReps}</p>}
                            {ex.tip && <p className="exercise-tip">{ex.tip}</p>}
                          </div>
                          <button type="button" className="btn-icon" onClick={() => onDeleteExercise(i)} aria-label={t.deleteExercise}>
                            <IconTrash size={16} />
                          </button>
                        </div>

                        <div className="set-table">
                          <div className={`set-table-head${timed ? ' is-timed' : ''}${advancedTracking && !timed ? ' is-advanced' : ''}`}>
                            <span>{t.set}</span>
                            <span>{t.previous}</span>
                            {timed ? <span>{t.minutes}</span> : (
                              <>
                                <span>{t.reps}</span>
                                <span>{weightUnit}</span>
                                {advancedTracking && <span>{t.rir}</span>}
                              </>
                            )}
                            <span />
                          </div>
                          {(ex.sets || []).map((s, j) => {
                            const done = isSetComplete(ex, s);
                            const prev = last[j] || last[last.length - 1];
                            return (
                              <div key={j} className={`set-log-row${done ? ' is-done' : ''}${timed ? ' is-timed' : ''}${advancedTracking && !timed ? ' is-advanced' : ''}`}>
                                <span className="set-badge">{j + 1}</span>
                                <button
                                  type="button"
                                  className="set-prev set-prev-btn"
                                  disabled={done || !prev}
                                  onClick={() => onFillFromPrevious(i, j)}
                                  aria-label={t.copyPrevious}
                                >
                                  {previousLabel(prev, weightUnit, timed)}
                                </button>
                                {timed ? (
                                  <input
                                    className="set-input"
                                    inputMode="decimal"
                                    placeholder={prev?.durationMinutes || t.minutes}
                                    value={s.durationMinutes || ''}
                                    onChange={(e) => onUpdateSet(i, j, 'durationMinutes', e.target.value)}
                                  />
                                ) : (
                                  <>
                                    <input
                                      className="set-input"
                                      inputMode="numeric"
                                      placeholder={prev?.reps || t.reps}
                                      value={s.reps || ''}
                                      onChange={(e) => onUpdateSet(i, j, 'reps', e.target.value)}
                                    />
                                    <input
                                      className="set-input"
                                      inputMode="decimal"
                                      placeholder={prev?.weight || weightUnit}
                                      value={s.weight || ''}
                                      onChange={(e) => onUpdateSet(i, j, 'weight', e.target.value)}
                                    />
                                    {advancedTracking && (
                                      <input
                                        className="set-input"
                                        inputMode="numeric"
                                        placeholder={t.rir}
                                        value={s.rir || ''}
                                        onChange={(e) => onUpdateSet(i, j, 'rir', e.target.value)}
                                      />
                                    )}
                                  </>
                                )}
                                <div className="set-row-actions">
                                  <button
                                    type="button"
                                    className={`set-check${done ? ' is-complete' : ''}`}
                                    onClick={() => onToggleComplete(i, j)}
                                    disabled={!done && !isSetFilled(ex, s)}
                                    aria-label={done ? t.uncompleteSet : t.completeSet}
                                  >
                                    <IconCheck size={14} />
                                  </button>
                                  <button type="button" className="set-remove" onClick={() => onDeleteSet(i, j)} aria-label={t.removeSet}>
                                    <IconClose size={12} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <button type="button" className="btn btn-dark btn-add-set" onClick={() => onAddSetRow(i)}>
                          <IconPlus size={14} /> {t.addSet2}
                        </button>
                      </div>
                    );
                  })}
                  <button type="button" className="btn btn-finish" onClick={onFinish} disabled={!hasSets}>
                    {t.finishWorkout}
                  </button>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
